import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { colors, spacing, shadows, typography, typographyFlat, borderRadius } from '../../styles/tokens';
import { SkeletonLoader } from './SkeletonLoader';
import { MTGJSONMetadataDisplay } from '../system/MTGJSONMetadataDisplay';
import { usePendingOrders } from '../../hooks/usePendingOrders';
import { useAuth } from '../../hooks/useAuth';
import { getAppEnv, getEnvColors } from '../../config/runtimeConfig';
import {
  DEFAULT_INVENTORY_LOOKUP_PATH,
  INVENTORY_LOOKUP_GAMES,
  INVENTORY_SEALED_LOOKUP,
  INVENTORY_SHARED_SECTIONS,
  inventorySharedHref,
} from '../../lib/inventoryNav';

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
  loading?: boolean;
  panelClassName?: string;
}

interface NavChildItem {
  id: string;
  label: string;
  path: string;
  matchPrefix?: string;
}

interface NavItem {
  id: string;
  label: string;
  icon: string;
  path: string;
  children?: NavChildItem[];
  matchPrefix?: string;
}

const navItems: NavItem[] = [
  {
    id: 'inventory',
    label: 'Inventory',
    icon: '📦',
    path: DEFAULT_INVENTORY_LOOKUP_PATH,
    matchPrefix: '/inventory',
    children: [
      ...INVENTORY_LOOKUP_GAMES.map((game) => ({
        id: `inventory-${game.id}`,
        label: game.label,
        path: game.path,
      })),
      {
        id: 'inventory-sealed',
        label: INVENTORY_SEALED_LOOKUP.label,
        path: INVENTORY_SEALED_LOOKUP.path,
      },
      ...INVENTORY_SHARED_SECTIONS.map((section) => ({
        id: `inventory-${section.id}`,
        label: section.label,
        path: section.path,
      })),
    ],
  },
  { id: 'pricing', label: 'Pricing', icon: '💰', path: '/pricing' },
  {
    id: 'orders',
    label: 'Orders',
    icon: '📋',
    path: '/orders/combined',
    matchPrefix: '/orders',
    children: [
      { id: 'orders-combined', label: 'Combined', path: '/orders/combined' },
      { id: 'orders-manapool', label: 'Manapool', path: '/orders/manapool' },
      { id: 'orders-shopify', label: 'Shopify', path: '/orders/shopify' },
      { id: 'orders-bans', label: 'Ban list', path: '/orders/bans' },
      {
        id: 'orders-order-pull',
        label: 'Order pull',
        path: '/orders/order-pull',
        matchPrefix: '/orders/order-pull',
      },
    ],
  },
  {
    id: 'time-clock',
    label: 'Time Clock',
    icon: '⏱️',
    path: '/time/clock',
    matchPrefix: '/time',
    children: [
      { id: 'time-clock-main', label: 'Clock In/Out', path: '/time/clock' },
      { id: 'employee-management', label: 'Employee Management', path: '/time/employees' },
    ],
  },
  {
    id: 'channels',
    label: 'Channels',
    icon: '🔗',
    path: '/channels/combined',
    matchPrefix: '/channels',
    children: [
      { id: 'channels-combined', label: 'Combined', path: '/channels/combined' },
      { id: 'channels-manapool', label: 'Manapool', path: '/channels/manapool' },
      { id: 'channels-shopify', label: 'Shopify', path: '/channels/shopify' },
      { id: 'channels-tcgplayer', label: 'TCGPlayer', path: '/channels/tcgplayer' },
      { id: 'channel-approvals', label: 'Approvals', path: '/channels/approvals' },
    ],
  },
  { id: 'buylist', label: 'Buylist', icon: '💵', path: '/buylist', matchPrefix: '/buylist' },
  { id: 'ck-tradein', label: 'CK Trade-In', icon: '💵', path: '/ck-tradein', matchPrefix: '/ck-tradein' },
  { id: 'settings', label: 'Settings', icon: '⚙️', path: '/settings', matchPrefix: '/settings' },
  {
    id: 'reports',
    label: 'Reports',
    icon: '📈',
    path: '/reports/channel-parity',
    matchPrefix: '/reports',
    children: [
      // Confidence
      { id: 'reports-channel-parity', label: 'Channel Parity', path: '/reports/channel-parity' },
      { id: 'reports-listing-coverage', label: 'Listing Coverage', path: '/reports/listing-coverage' },
      { id: 'reports-shipped-shortfall', label: 'Shipped Shortfall w/ Stock', path: '/reports/shipped-shortfall-with-stock' },
      // History
      { id: 'reports-reconciliation', label: 'Inbound recon history', path: '/reports/reconciliation' },
      // Pipeline
      { id: 'reports-sync-reliability', label: 'Sync Reliability', path: '/reports/sync-reliability' },
      { id: 'reports-outbox-reliability', label: 'Outbox Reliability', path: '/reports/outbox-reliability' },
      { id: 'reports-fanout-slo-breaches', label: 'Fanout SLO Breaches', path: '/reports/fanout-slo-breaches' },
      // Ops
      { id: 'reports-fulfillment-aging', label: 'Fulfillment Aging', path: '/reports/fulfillment-aging' },
      { id: 'reports-pull-list', label: 'High-value inventory', path: '/reports/pull-list' },
      { id: 'reports-inventory-value', label: 'Inventory value', path: '/reports/inventory-value' },
      { id: 'reports-sales-overview', label: 'Sales overview', path: '/reports/sales-overview' },
      { id: 'reports-sales-cogs', label: 'Sales / COGS', path: '/reports/sales-cogs' },
      { id: 'reports-shrink', label: 'Shrink', path: '/reports/shrink' },
      { id: 'reports-weekly-hours', label: 'Weekly Employee Hours', path: '/reports/weekly-hours' },
    ],
  },
  { id: 'dashboards', label: 'Dashboards', icon: '📊', path: '/dashboards' },
];

export const Sidebar: React.FC<SidebarProps> = ({
  collapsed,
  onToggle,
  loading = false,
  panelClassName = 'app-sidebar',
}) => {
  const location = useLocation();
  const { pendingCount } = usePendingOrders();
  const { user, logout } = useAuth();
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});
  const appEnv = getAppEnv();
  const envColors = getEnvColors();

  const isChildPathActive = (child: NavChildItem) => {
    if (location.pathname === child.path) {
      return true;
    }
    return Boolean(child.matchPrefix) && location.pathname.startsWith(`${child.matchPrefix}/`);
  };

  const isNavItemActive = (item: NavItem) => {
    if (location.pathname === item.path) {
      return true;
    }

    if (item.matchPrefix && location.pathname.startsWith(item.matchPrefix)) {
      return true;
    }

    return item.children?.some((child) => isChildPathActive(child)) ?? false;
  };

  const activeItem = navItems.find(isNavItemActive)?.id || 'inventory';

  const toggleSection = (sectionId: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }));
  };

  const handleLogout = async () => {
    try {
      await logout();
      window.location.href = '/login';
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  if (loading) {
    return (
    <aside className={panelClassName} style={{ ...styles.sidebar, width: collapsed ? `calc(${spacing.xs} + ${spacing.sm} + ${spacing.sm})` : `calc(${spacing.lg} * 10)` }}>
        <div style={styles.header}>
          <SkeletonLoader width="80%" height={`calc(${spacing.md} * 1.5)`} />
        </div>
        <nav style={styles.nav}>
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <SkeletonLoader key={i} width="100%" height={`${spacing.lg}`} style={{ marginBottom: spacing.sm }} />
          ))}
        </nav>
      </aside>
    );
  }

  return (
    <>
      <aside
        className={panelClassName}
        style={{
          ...styles.sidebar,
          width: collapsed ? `calc(${spacing.xs} + ${spacing.sm} + ${spacing.sm})` : `calc(${spacing.lg} * 10)`,
          backgroundColor: envColors.sidebarBackground,
          borderRight: `2px solid ${envColors.sidebarBorder}`,
        }}
      >
        <div style={{ ...styles.header, borderBottom: `1px solid ${envColors.sidebarBorder}` }}>
          {!collapsed && (
            <div style={{ display: 'flex', flexDirection: 'column' as const, gap: spacing.xs }}>
              <h1 style={{ ...styles.title, color: envColors.sidebarText }}>TCG Admin</h1>
              {appEnv !== 'prod' && (
                <span style={{
                  display: 'inline-block',
                  padding: `2px ${spacing.sm}`,
                  borderRadius: borderRadius.sm,
                  backgroundColor: envColors.badge,
                  color: envColors.badgeText,
                  fontSize: typography.caption.fontSize,
                  fontWeight: typographyFlat.fontWeight.bold,
                  letterSpacing: '0.05em',
                  textTransform: 'uppercase' as const,
                  alignSelf: 'flex-start',
                }}>
                  {appEnv}
                </span>
              )}
            </div>
          )}
          <button
            onClick={onToggle}
            style={styles.toggleButton}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {collapsed ? '☰' : '✕'}
          </button>
        </div>

        <nav style={styles.nav}>
          {navItems.map((item) => {
            const isActive = activeItem === item.id;
            const hasChildren = Boolean(item.children?.length);
            const isExpanded = isActive || Boolean(expandedSections[item.id]);

            return (
              <div key={item.id} style={styles.navGroup}>
                <div style={styles.navGroupHeader}>
                  <Link
                    to={item.path}
                    style={{
                      ...styles.navItem,
                      ...(isActive ? styles.navItemActive : {}),
                      ...(hasChildren && !collapsed ? styles.navItemWithCaret : {}),
                      textDecoration: 'none',
                    }}
                    title={collapsed ? item.label : undefined}
                  >
                    <span style={styles.navIcon}>{item.icon}</span>
                    {!collapsed && (
                      <span style={styles.navLabel}>
                        {item.label}
                        {item.id === 'orders' && pendingCount > 0 && (
                          <span style={styles.navBadge}>{pendingCount}</span>
                        )}
                      </span>
                    )}
                  </Link>
                  {!collapsed && hasChildren && (
                    <button
                      type="button"
                      onClick={() => toggleSection(item.id)}
                      style={styles.expandButton}
                      aria-label={isExpanded ? `Collapse ${item.label}` : `Expand ${item.label}`}
                    >
                      {isExpanded ? '▾' : '▸'}
                    </button>
                  )}
                </div>

                {!collapsed && hasChildren && isExpanded && (
                  <div style={styles.childNav}>
                    {item.children?.map((child) => {
                      const isChildActive = isChildPathActive(child);
                      return (
                        <Link
                          key={child.id}
                          to={inventorySharedHref(child.path, location.pathname)}
                          style={{
                            ...styles.childNavItem,
                            ...(isChildActive ? styles.childNavItemActive : {}),
                            textDecoration: 'none',
                          }}
                        >
                          {child.label}
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        <div style={styles.footer}>
          {!collapsed && <MTGJSONMetadataDisplay />}
          {!collapsed && (
            <div style={styles.userInfo}>
              <div style={styles.avatar}>👤</div>
              <div style={styles.userDetails}>
                <div style={styles.userName}>{user?.username || 'Unknown User'}</div>
              </div>
              <button type="button" style={styles.logoutButton} onClick={handleLogout}>
                Logout
              </button>
            </div>
          )}
        </div>
      </aside>
      
      {/* Floating expand button when collapsed */}
      {collapsed && (
        <button
          onClick={onToggle}
          style={styles.floatingExpandButton}
          aria-label="Expand sidebar"
          title="Expand sidebar"
        >
          ☰
        </button>
      )}
    </>
  );
};

const styles = {
  sidebar: {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    height: '100vh',
    backgroundColor: colors.surface,
    borderRight: `1px solid ${colors.border}`,
    boxShadow: shadows.md,
    display: 'flex',
    flexDirection: 'column' as const,
    transition: 'width 0.3s ease',
    zIndex: 1000,
    overflow: 'hidden',
  },
  header: {
    padding: spacing.lg,
    borderBottom: `1px solid ${colors.border}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    minHeight: spacing['3xl'],
  },
  title: {
    fontSize: typography.h1.fontSize,
    fontWeight: typography.h1.fontWeight,
    color: colors.text,
    margin: 0,
  },
  toggleButton: {
    background: 'none',
    border: 'none',
    fontSize: typographyFlat.fontSize.lg,
    cursor: 'pointer',
    padding: spacing.sm,
    color: colors.textSecondary,
    transition: 'color 0.2s',
    ':hover': {
      color: colors.text,
    },
  },
  nav: {
    flex: 1,
    padding: spacing.md,
    overflowY: 'auto' as const,
  },
  navGroup: {
    marginBottom: spacing.xs,
  },
  navGroupHeader: {
    position: 'relative' as const,
    display: 'flex',
    alignItems: 'center',
  },
  navItem: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    gap: spacing.md,
    padding: spacing.md,
    marginBottom: spacing.sm,
    border: 'none',
    backgroundColor: 'transparent',
    borderRadius: spacing.sm,
    cursor: 'pointer',
    transition: 'all 0.2s',
    color: colors.textSecondary,
    fontSize: typography.body.fontSize,
    textAlign: 'left' as const,
  },
  navItemActive: {
    backgroundColor: colors.primaryLight + '20',
    color: colors.primary,
    fontWeight: typographyFlat.fontWeight.semibold,
  },
  navItemWithCaret: {
    paddingRight: `calc(${spacing.lg} + ${spacing.sm})`,
  },
  navIcon: {
    fontSize: '20px',
    flexShrink: 0,
  },
  navLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing.sm,
    whiteSpace: 'nowrap' as const,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  navBadge: {
    padding: `${spacing.xs} ${spacing.sm}`,
    borderRadius: borderRadius.sm,
    backgroundColor: colors.error,
    color: colors.white,
    fontSize: typography.caption.fontSize,
    fontWeight: typographyFlat.fontWeight.bold,
  },
  expandButton: {
    position: 'absolute' as const,
    right: spacing.sm,
    top: '50%',
    transform: 'translateY(-50%)',
    border: 'none',
    backgroundColor: 'transparent',
    color: colors.textSecondary,
    fontSize: typographyFlat.fontSize.lg,
    cursor: 'pointer',
    padding: `${spacing.xs} ${spacing.xs}`,
    borderRadius: borderRadius.sm,
    lineHeight: 1,
  },
  childNav: {
    marginLeft: `calc(${spacing.md} + ${spacing.xs})`,
    borderLeft: `1px solid ${colors.border}`,
    paddingLeft: spacing.sm,
    marginBottom: spacing.xs,
  },
  childNavItem: {
    display: 'block',
    color: colors.textSecondary,
    fontSize: typography.small.fontSize,
    padding: `${spacing.xs} ${spacing.sm}`,
    borderRadius: borderRadius.sm,
    marginBottom: spacing.xs,
  },
  childNavItemActive: {
    backgroundColor: colors.primaryLight + '1a',
    color: colors.primary,
    fontWeight: typographyFlat.fontWeight.semibold,
  },
  footer: {
    padding: spacing.md,
    borderTop: `1px solid ${colors.border}`,
  },
  userInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing.sm,
  },
  avatar: {
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    backgroundColor: colors.gray200,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '16px',
  },
  userDetails: {
    flex: 1,
    overflow: 'hidden',
  },
  userName: {
    fontSize: typography.small.fontSize,
    fontWeight: typographyFlat.fontWeight.semibold,
    color: colors.text,
    whiteSpace: 'nowrap' as const,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  logoutButton: {
    border: `1px solid ${colors.border}`,
    backgroundColor: colors.surface,
    color: colors.textSecondary,
    borderRadius: borderRadius.sm,
    fontSize: typography.caption.fontSize,
    padding: `${spacing.xs} ${spacing.sm}`,
    cursor: 'pointer',
    flexShrink: 0,
  },
  floatingExpandButton: {
    position: 'fixed' as const,
    top: spacing.md,
    left: `calc(${spacing.xs} + ${spacing.sm} + ${spacing.sm} + ${spacing.sm})`,
    width: `calc(${spacing.lg} * 2)`,
    height: `calc(${spacing.lg} * 2)`,
    borderRadius: '50%',
    backgroundColor: colors.primary,
    color: colors.white,
    border: 'none',
    boxShadow: shadows.lg,
    fontSize: typographyFlat.fontSize.xl,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 999,
    transition: 'transform 0.2s, box-shadow 0.2s',
    ':hover': {
      transform: 'scale(1.1)',
      boxShadow: shadows.xl,
    },
  },
};
